import React, { useRef, useEffect, useState } from 'react';
import { Input, Button, message } from 'antd';
import styled from 'styled-components';
import { SendOutlined, UserOutlined, DownOutlined } from '@ant-design/icons';
import { ProjectStage, STAGE_NAMES } from '../../constants/stages';
import { Project } from '../../types/project';
import { chat } from '../../services/api';
import { socketService, WS_EVENTS } from '../../services/socket';
import StageTabs from './StageTabs';

const Container = styled.div`
  display: flex;
  height: 100%;
  gap: 32px;
  padding: 16px 32px 24px;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  height: 100%;
`;

const StageHeader = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const BackButton = styled(Button)`
  position: absolute;
  left: 16px;
  padding: 4px 8px;
  height: 28px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
  transition: all 0.3s;

  &:hover {
    color: #1890ff;
    background: rgba(24, 144, 255, 0.1);
  }
`;

const StageTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1f2937;
`;

const ChatContainer = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  flex-shrink: 0;
`;

const ChatMainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  min-width: 0;
  padding-top: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  margin-bottom: 80px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #e8e8e8;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const MessageWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

const MessageRow = styled.div<{ $isUser?: boolean }>`
  display: flex;
  flex-direction: ${props => props.$isUser ? 'row-reverse' : 'row'};
  align-items: flex-start;
  gap: 8px;
`;

const MessageContentWrapper = styled.div<{ $isUser?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  max-width: 80%;
`;

const MessageContent = styled.div<{ $isUser?: boolean; $isError?: boolean }>`
  padding: 12px 16px;
  border-radius: ${props => props.$isUser ? '12px 2px 12px 12px' : '2px 12px 12px 12px'};
  background: ${props => props.$isError ? '#fff2f0' : props.$isUser ? '#1890ff' : '#f5f5f5'};
  color: ${props => props.$isError ? '#ff4d4f' : props.$isUser ? '#fff' : 'rgba(0, 0, 0, 0.85)'};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  ${props => props.$isError && `
    border: 1px solid #ffccc7;
  `}
`;

const Avatar = styled.div<{ $isUser?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$isUser ? '#1890ff' : '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const { TextArea } = Input;

const StyledTextArea = styled(TextArea)`
  &.ant-input {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 8px;
    resize: none;
    min-height: 40px;
    max-height: 120px;
  }

  &.ant-input[disabled] {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const InputContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const SendButton = styled(Button)`
  border-radius: 8px;
`;

const ScrollToBottomButton = styled(Button)`
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 20px;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8e8e8;
  
  &:hover {
    background: #fafafa;
  }
`;

const LoadingMessage = styled(MessageContent)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.45);
`;

const ThinkingMessage = styled(MessageContent)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.45);
`;

const ThinkingDots = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;

  span {
    width: 4px;
    height: 4px;
    background: currentColor;
    border-radius: 50%;
    animation: thinking 1.4s infinite;
    opacity: 0.3;
  }

  @keyframes thinking {
    0%, 100% {
      opacity: 0.3;
    }
    50% {
      opacity: 1;
    }
  }
`;

// 创建一个固定的dots数组，避免在渲染时重新创建
const THINKING_DOTS = [
  { id: 'dot-1', delay: 0 },
  { id: 'dot-2', delay: 0.2 },
  { id: 'dot-3', delay: 0.4 }
];

const ThinkingDot = styled.span<{ $delay: number }>`
  animation-delay: ${props => props.$delay}s !important;
`;

// 添加工具调用相关的类型定义
interface ToolCall {
  name: string;
  parameters: Record<string, any>;
}

interface ChatMessage {
  content: string;
  role: 'user' | 'assistant' | 'thinking';
  timestamp: string;
  id?: string;
  projectId?: string;
  stage?: number;
  is_final?: boolean;
  tool_calls?: ToolCall[];
  type?: string;
}

interface ChatHistory {
  id: string;
  userId: string;
  projectId: string;
  stage: number;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  isArchived: boolean;
}

interface ChatViewProps {
  selectedStage: ProjectStage;
  currentProject: Project | null;
}

// 添加工具调用内容的渲染组件
const ToolCallContent = styled.div`
  background: #f8f9fa;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;
  font-family: monospace;
  font-size: 12px;
  color: #666;
  white-space: pre-wrap;
`;

export const ChatView: React.FC<ChatViewProps> = ({
  selectedStage,
  currentProject
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingMessageId] = useState(() => Math.random().toString(36).substring(7));
  const lastMessageTimeRef = useRef<number>(0);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasReceivedGreeting, setHasReceivedGreeting] = useState(false);

  const setupEventListeners = (isMounted: boolean) => {
    const disconnectHandler = () => {
      if (isMounted) {
        console.log('Disconnected from chat server');
      }
    };

    const connectErrorHandler = (error: Error) => {
      if (isMounted) {
        console.error('Connection error:', error);
        message.error('连接服务器失败，请检查网络连接');
      }
    };

    const messageHandler = (msg: ChatMessage) => {
      if (msg.projectId === currentProject?.id && msg.stage === selectedStage) {
        if (isMounted) {
          setMessages(prev => {
            if (msg.is_final) {
              return prev.filter(m => m.role !== 'thinking').concat(msg);
            }
            return prev.concat(msg);
          });
          
          if (msg.is_final) {
            setIsWaitingResponse(false);
            setIsThinking(false);
            setIsLoading(false);
            if (!hasReceivedGreeting && msg.role === 'assistant') {
              setHasReceivedGreeting(true);
            }
          }
          
          if (isAtBottom) {
            scrollToBottom();
          }
        }
      }
    };

    const errorHandler = (error: any) => {
      if (isMounted) {
        console.error('Chat error:', error);
        // 将错误消息添加到消息列表中
        setMessages(prev => {
          // 移除思考中的消息
          const filteredMessages = prev.filter(m => m.role !== 'thinking');
          // 添加错误消息
          return [...filteredMessages, {
            content: error.content || '发生错误，请重试',
            role: 'assistant',
            type: 'error',
            timestamp: new Date().toISOString(),
            id: Math.random().toString(36).substring(7),
            projectId: currentProject?.id,
            stage: selectedStage,
            is_final: true
          }];
        });
        setIsWaitingResponse(false);
        setIsThinking(false);
        setIsLoading(false);
      }
    };

    socketService.on(WS_EVENTS.DISCONNECT, disconnectHandler);
    socketService.on(WS_EVENTS.CONNECT_ERROR, connectErrorHandler);
    socketService.on(WS_EVENTS.CHAT_MESSAGE, messageHandler);
    socketService.on(WS_EVENTS.CHAT_ERROR, errorHandler);

    // 返回清理函数
    return () => {
      socketService.off(WS_EVENTS.DISCONNECT, disconnectHandler);
      socketService.off(WS_EVENTS.CONNECT_ERROR, connectErrorHandler);
      socketService.off(WS_EVENTS.CHAT_MESSAGE, messageHandler);
      socketService.off(WS_EVENTS.CHAT_ERROR, errorHandler);
    };
  };

  useEffect(() => {
    let isMounted = true;
    let shouldInitChat = false;
    let cleanup: (() => void) | undefined;
    let connectHandler: (() => void) | undefined;

    const initializeChat = async () => {
      try {
        setIsInitializing(true);
        
        // 1. 先加载历史消息
        const hasHistory = await loadChatHistory();
        
        // 2. 如果没有历史记录，标记需要初始化聊天
        if (!hasHistory) {
          shouldInitChat = true;
        }
        
        // 3. 建立WebSocket连接
        if (isMounted) {
          socketService.connect();
          
          // 4. 设置事件监听器
          connectHandler = () => {
            console.log('Connected to chat server');
            // 5. 在连接建立后，如果需要初始化聊天，发送初始化事件
            if (shouldInitChat && currentProject?.id && !hasInitialized) {
              socketService.emit(WS_EVENTS.INIT_CHAT, {
                projectId: currentProject.id,
                stage: selectedStage
              });
              setHasInitialized(true);
            }
          };
          
          socketService.on(WS_EVENTS.CONNECT, connectHandler);
          
          // 设置其他事件监听器并保存清理函数
          cleanup = setupEventListeners(isMounted);
        }
      } catch (error) {
        console.error('初始化聊天失败:', error);
        if (isMounted) {
          message.error('初始化聊天失败，请刷新页面重试');
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    // 如果有项目ID，开始初始化
    if (currentProject?.id) {
      initializeChat();
    }

    // 清理函数
    return () => {
      isMounted = false;
      if (cleanup) {
        cleanup();
      }
      if (connectHandler) {
        socketService.off(WS_EVENTS.CONNECT, connectHandler);
      }
      socketService.disconnect();
    };
  }, [currentProject?.id, selectedStage]);

  const loadChatHistory = async () => {
    try {
      if (!currentProject?.id) return false;
      setIsInitializing(true);
      
      const histories: ChatHistory[] = await chat.getHistory(currentProject.id, selectedStage);
      const activeHistory = histories.find(h => !h.isArchived);
      
      if (!activeHistory) {
        setMessages([{
          content: "AI 助手正在准备中...",
          role: "thinking",
          timestamp: new Date().toISOString(),
          id: thinkingMessageId,
          projectId: currentProject.id,
          stage: selectedStage,
          is_final: false
        }]);
        setHasReceivedGreeting(false);
        return false;
      } else {
        if (activeHistory.messages && activeHistory.messages.length > 0) {
          setMessages(activeHistory.messages.map(msg => ({
            ...msg,
            id: msg.id || Math.random().toString(36).substring(7),
            projectId: activeHistory.projectId,
            stage: activeHistory.stage,
            is_final: true
          })));
          setIsThinking(false);
          // 检查是否有助手的欢迎消息
          const hasAssistantMessage = activeHistory.messages.some(msg => msg.role === 'assistant');
          setHasReceivedGreeting(hasAssistantMessage);
        }
        return true;
      }
    } catch (error: any) {
      console.error('加载历史消息失败:', error);
      setMessages([{
        content: "加载历史消息失败",
        role: "assistant",
        type: "error",
        timestamp: new Date().toISOString(),
        id: Math.random().toString(36).substring(7),
        projectId: currentProject?.id,
        stage: selectedStage,
        is_final: true
      }]);
      message.error('加载历史消息失败，请刷新页面重试');
      return false;
    } finally {
      setIsInitializing(false);
    }
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const checkIfAtBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const { scrollHeight, scrollTop, clientHeight } = container;
      const isBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
      setIsAtBottom(isBottom);
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkIfAtBottom);
      return () => container.removeEventListener('scroll', checkIfAtBottom);
    }
  }, []);

  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom('auto');
    }
  }, [messages]);

  const handleSend = async () => {
    if (!hasReceivedGreeting || !inputValue.trim() || isLoading || !currentProject?.id) return;
    
    // 格式化消息内容，处理换行
    const formattedContent = inputValue.trim().replace(/\n{3,}/g, '\n\n'); // 将连续3个以上换行替换为2个
    
    const userMessage: ChatMessage = {
      content: formattedContent,
      role: 'user',
      timestamp: new Date().toISOString(),
      id: Math.random().toString(36).substring(7),
      projectId: currentProject.id,
      stage: selectedStage,
      is_final: true
    };
    
    lastMessageTimeRef.current = Date.now();
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsThinking(true);

    // 添加思考中的消息
    const thinkingMessage: ChatMessage = {
      content: "思考中",
      role: "thinking",
      timestamp: new Date().toISOString(),
      id: thinkingMessageId,
      projectId: currentProject.id,
      stage: selectedStage,
      is_final: false
    };
    
    setMessages(prev => [...prev, thinkingMessage]);
    
    try {
      socketService.emit(WS_EVENTS.CHAT_MESSAGE, {
        message: formattedContent,
        projectId: currentProject.id,
        stage: selectedStage
      });
    } catch (error: any) {
      console.error('发送消息失败:', error);
      message.error('发送消息失败，请重试');
      setIsLoading(false);
      setIsThinking(false);
      setMessages(prev => prev.filter(msg => msg.id !== thinkingMessageId && msg.id !== userMessage.id));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!hasReceivedGreeting) return;
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Container>
      <MainContent>
        <StageHeader>
          <BackButton>
            <UserOutlined />
            返回
          </BackButton>
          <StageTitle>{STAGE_NAMES[selectedStage]}阶段</StageTitle>
        </StageHeader>
        <StageTabs 
          stage={selectedStage} 
          projectId={currentProject?.id}
        />
      </MainContent>
      <ChatContainer>
        <ChatMainArea>
          <MessagesContainer ref={messagesContainerRef}>
            {messages.map((msg) => {
              const messageId = msg.id || `msg-${Math.random().toString(36).substring(7)}`;
              
              return (
                <MessageWrapper key={messageId} role="article">
                  <MessageRow $isUser={msg.role === 'user'}>
                    <Avatar $isUser={msg.role === 'user'}>
                      {msg.role === 'user' ? <UserOutlined /> : 'AI'}
                    </Avatar>
                    <MessageContentWrapper $isUser={msg.role === 'user'}>
                      {msg.role === 'thinking' ? (
                        <ThinkingMessage>
                          <span>{msg.content}</span>
                          <ThinkingDots>
                            {THINKING_DOTS.map(dot => (
                              <ThinkingDot key={dot.id} $delay={dot.delay} />
                            ))}
                          </ThinkingDots>
                        </ThinkingMessage>
                      ) : (
                        <>
                          <MessageContent 
                            $isUser={msg.role === 'user'} 
                            $isError={msg.type === 'error'}
                          >
                            {msg.content}
                          </MessageContent>
                          {msg.tool_calls && msg.tool_calls.length > 0 && (
                            <ToolCallContent>
                              {msg.tool_calls.map((tool, index) => (
                                <div key={`${messageId}-tool-${index}`}>
                                  {`> 执行工具: ${tool.name}`}
                                  {`\n参数: ${JSON.stringify(tool.parameters, null, 2)}`}
                                </div>
                              ))}
                            </ToolCallContent>
                          )}
                        </>
                      )}
                    </MessageContentWrapper>
                  </MessageRow>
                </MessageWrapper>
              );
            })}
            <div ref={messagesEndRef} />
          </MessagesContainer>
          {!isAtBottom && (
            <ScrollToBottomButton
              type="text"
              icon={<DownOutlined />}
              onClick={() => scrollToBottom()}
            />
          )}
          <InputContainer>
            <StyledTextArea
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={hasReceivedGreeting ? "输入消息，Shift + Enter换行..." : "等待 AI 助手初始化..."}
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
            <SendButton
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!hasReceivedGreeting || !inputValue.trim() || isInitializing || isThinking}
            >
              发送
            </SendButton>
          </InputContainer>
        </ChatMainArea>
      </ChatContainer>
    </Container>
  );
};

export default ChatView; 